/**
 * Supabase persistence for vehicle analyses and value snapshots.
 * Write functions now THROW on failure so callers can queue retries.
 * All loads are for hydration when localStorage is empty.
 */
import { supabase } from '../config/supabase';

/* ───────────────────── Vehicle Analyses ───────────────────── */

/**
 * Upsert a full analysis to Supabase via RPC (bypasses RLS).
 * Uses upsert_vehicle_analysis RPC so collaborators can update shared vehicle analyses.
 * Maps the normalized analysis format (from Processing.jsx) to column names.
 */
export async function saveAnalysisToSupabase(userId, vehicleId, analysis) {
  const { error } = await supabase.rpc('upsert_vehicle_analysis', {
    v_user_id: userId,
    v_vehicle_id: vehicleId,
    v_health_score: typeof analysis.healthScore === 'number' ? analysis.healthScore : null,
    v_health_summary: analysis.healthSummary || null,
    v_maintenance_items: analysis.maintenanceItems || [],
    v_market_value: analysis.marketValue || {},
    v_vehicle_summary: analysis.vehicleSummary || {},
    v_analyzed_at: analysis.analyzedAt || new Date().toISOString(),
  });

  if (error) throw error;
  console.log('[PM-Write] Saved analysis to Supabase for vehicle:', vehicleId);
}

/**
 * Update only the maintenance section of an analysis in Supabase via RPC (bypasses RLS).
 * Uses update_vehicle_maintenance RPC so collaborators can update shared vehicle analyses.
 */
export async function updateMaintenanceInSupabase(userId, vehicleId, maintenanceItems, healthScore, healthSummary) {
  const { error } = await supabase.rpc('update_vehicle_maintenance', {
    v_vehicle_id: vehicleId,
    v_maintenance_items: maintenanceItems,
    v_health_score: healthScore,
    v_health_summary: healthSummary,
  });

  if (error) throw error;
  console.log('[PM-Write] Updated maintenance in Supabase for vehicle:', vehicleId);
}

/**
 * Update only the market value section of an analysis in Supabase via RPC (bypasses RLS).
 * Uses update_vehicle_valuation RPC so collaborators can update shared vehicle analyses.
 */
export async function updateValuationInSupabase(userId, vehicleId, marketValue) {
  const { error } = await supabase.rpc('update_vehicle_valuation', {
    v_vehicle_id: vehicleId,
    v_market_value: marketValue,
  });

  if (error) throw error;
  console.log('[PM-Write] Updated valuation in Supabase for vehicle:', vehicleId);
}

/**
 * Load all analyses for a user from Supabase (for hydration).
 * Returns object keyed by vehicle_id in the localStorage analysis format.
 */
export async function loadUserAnalyses(userId) {
  try {
    const { data, error } = await supabase
      .from('vehicle_analyses')
      .select('*')
      .eq('user_id', userId);

    if (error) throw error;
    if (!data || data.length === 0) return {};

    const result = {};
    for (const row of data) {
      result[row.vehicle_id] = {
        vehicleSummary: row.vehicle_summary || {},
        healthScore: row.health_score,
        healthSummary: row.health_summary,
        maintenanceItems: row.maintenance_items || [],
        marketValue: row.market_value || {},
        analyzedAt: row.analyzed_at,
      };
    }
    console.log('[PM-Read] Loaded', data.length, 'analyses from Supabase');
    return result;
  } catch (err) {
    console.error('[PM-Read] Failed to load analyses:', err.message);
    return {};
  }
}

/* ───────────────────── Value Snapshots ───────────────────── */

/**
 * Insert a value snapshot to Supabase.
 */
export async function saveValueSnapshotToSupabase(userId, vehicleId, snapshot) {
  const row = {
    user_id: userId,
    vehicle_id: vehicleId,
    market_value_low: snapshot.marketValueLow || null,
    market_value_high: snapshot.marketValueHigh || null,
    health_score: typeof snapshot.healthScore === 'number' ? snapshot.healthScore : null,
    snapshot_date: snapshot.date || new Date().toISOString(),
  };

  const { error } = await supabase
    .from('value_snapshots')
    .insert(row);

  if (error) throw error;
  console.log('[PM-Write] Saved value snapshot for vehicle:', vehicleId);
}

/**
 * Load a single vehicle's analysis via RPC (bypasses RLS).
 * Used for shared garage vehicles where the logged-in user may not be the analysis owner.
 * Returns a localStorage-format analysis object, or null if not found.
 *
 * @param {string} vehicleId - Vehicle UUID
 * @returns {Promise<object|null>}
 */
export async function loadVehicleAnalysis(vehicleId) {
  if (!vehicleId) return null;
  try {
    const { data, error } = await supabase.rpc('get_vehicle_analysis', {
      vehicle_uuid: vehicleId,
    });
    if (error) throw error;
    const row = Array.isArray(data) ? data[0] : data;
    if (!row) return null;

    console.log('[PM-Read] Loaded analysis for shared vehicle:', vehicleId);
    return {
      vehicleSummary: row.vehicle_summary || {},
      healthScore: row.health_score,
      healthSummary: row.health_summary,
      maintenanceItems: row.maintenance_items || [],
      marketValue: row.market_value || {},
      analyzedAt: row.analyzed_at,
    };
  } catch (err) {
    console.error('[PM-Read] loadVehicleAnalysis failed:', err.message);
    return null;
  }
}

/**
 * Load all value snapshots for a vehicle via RPC (bypasses RLS).
 * Used for shared garage vehicles where the logged-in user may not be the snapshot owner.
 * Returns an array of snapshot objects in localStorage format.
 *
 * @param {string} vehicleId - Vehicle UUID
 * @returns {Promise<Array>}
 */
export async function loadVehicleValueHistory(vehicleId) {
  if (!vehicleId) return [];
  try {
    const { data, error } = await supabase.rpc('get_vehicle_value_snapshots', {
      vehicle_uuid: vehicleId,
    });
    if (error) throw error;
    if (!data || data.length === 0) return [];

    console.log('[PM-Read] Loaded', data.length, 'value snapshots for vehicle:', vehicleId);
    return data.map((row) => ({
      marketValueLow: row.market_value_low,
      marketValueHigh: row.market_value_high,
      healthScore: row.health_score,
      date: row.snapshot_date,
    }));
  } catch (err) {
    console.error('[PM-Read] loadVehicleValueHistory failed:', err.message);
    return [];
  }
}

/**
 * Load all value snapshots for a user from Supabase (for hydration).
 * Returns object keyed by vehicle_id, each an array of snapshots sorted by date.
 */
export async function loadUserValueHistory(userId) {
  try {
    const { data, error } = await supabase
      .from('value_snapshots')
      .select('*')
      .eq('user_id', userId)
      .order('snapshot_date', { ascending: true });

    if (error) throw error;
    if (!data || data.length === 0) return {};

    const result = {};
    for (const row of data) {
      if (!result[row.vehicle_id]) result[row.vehicle_id] = [];
      result[row.vehicle_id].push({
        marketValueLow: row.market_value_low,
        marketValueHigh: row.market_value_high,
        healthScore: row.health_score,
        date: row.snapshot_date,
      });
    }
    // Cap at 50 per vehicle (matching localStorage behavior)
    for (const vid of Object.keys(result)) {
      if (result[vid].length > 50) result[vid] = result[vid].slice(-50);
    }
    console.log('[PM-Read] Loaded value history for', Object.keys(result).length, 'vehicles');
    return result;
  } catch (err) {
    console.error('[PM-Read] Failed to load value history:', err.message);
    return {};
  }
}
